import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';

// dd/mm/yyyy h:mm a. m. (el backend la manda en hora Venezuela, sin zona)
const formatFechaHora = (iso) => {
    if (!iso) return '';
    const d = new Date(iso.slice(0, 19));
    const dos = (n) => String(n).padStart(2, '0');
    const hora = `${d.getHours() % 12 || 12}:${dos(d.getMinutes())} ${d.getHours() < 12 ? 'a. m.' : 'p. m.'}`;
    return `${dos(d.getDate())}/${dos(d.getMonth() + 1)}/${d.getFullYear()} ${hora}`;
};

const COLOR_ORIGEN = {
    automatico: 'border-red-200 text-red-800 bg-red-50',
    manual: 'border-gray-200 text-gray-700 bg-white',
};

const TH = 'text-left text-xs font-semibold text-gray-600 uppercase tracking-wider px-4 py-3';

/**
 * Quien no puede comprar en ESTA venta. Los automaticos salen de las penalidades
 * del cierre sellado de la venta anterior; los manuales y los perdones los pone
 * Costos aqui. Nada de esto pasa a la venta siguiente.
 *
 * soloLectura: la venta esta cerrada; se ve la lista pero no se toca.
 */
const BloqueadosTab = ({ ventaId, soloLectura = false }) => {
    const [empleados, setEmpleados] = useState([]);
    const [venta, setVenta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busqueda, setBusqueda] = useState('');
    const [resultados, setResultados] = useState(null); // null = sin buscar todavia
    const [abierto, setAbierto] = useState(false);
    const [elegido, setElegido] = useState(null);
    const [motivo, setMotivo] = useState('');
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState('');

    const cargar = useCallback(async () => {
        try {
            const res = await axiosInstance.get(`/api/ventas-especiales/${ventaId}/bloqueados`);
            setEmpleados(res.data.empleados || []);
            setVenta(res.data.venta);
        } catch (e) {
            setError(e.response?.data?.error || 'No se pudieron cargar los bloqueados');
        } finally {
            setLoading(false);
        }
    }, [ventaId]);

    useEffect(() => { cargar(); }, [cargar]);

    useEffect(() => {
        const q = busqueda.trim();
        setResultados(null);
        if (elegido || q.length < 2) return undefined;
        const t = setTimeout(async () => {
            try {
                const res = await axiosInstance.get(`/api/ventas-especiales/${ventaId}/bloqueados`, { params: { q } });
                setResultados(res.data.resultados || []);
            } catch (e) { console.error(e); }
        }, 300);
        return () => clearTimeout(t);
    }, [busqueda, elegido, ventaId]);

    const elegir = (empleado) => {
        setElegido(empleado);
        setBusqueda('');
        setAbierto(false);
    };

    const bloquear = async (e) => {
        e.preventDefault();
        if (!elegido || !motivo.trim()) return;
        setGuardando(true);
        setError('');
        try {
            await axiosInstance.post(`/api/ventas-especiales/${ventaId}/bloqueados/${elegido.id}`, { motivo: motivo.trim() });
            setElegido(null);
            setMotivo('');
            cargar();
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo bloquear');
        } finally {
            setGuardando(false);
        }
    };

    const desbloquear = async (empleado) => {
        if (!window.confirm(`¿Desbloquear a ${empleado.nombre} en esta venta?`)) return;
        setError('');
        try {
            await axiosInstance.delete(`/api/ventas-especiales/${ventaId}/bloqueados/${empleado.id}`);
            cargar();
        } catch (err) {
            setError(err.response?.data?.error || 'No se pudo desbloquear');
        }
    };

    if (loading) {
        return <div className="flex items-center gap-2 py-16 justify-center text-sm text-gray-500"><Loader2 size={16} className="animate-spin" /> Cargando bloqueados</div>;
    }

    const anterior = venta?.anterior;
    const origen = !anterior
        ? 'Como no hay una venta anterior, no hay bloqueos automáticos.'
        : anterior.cierreSellado
            ? `Los automáticos salen de las penalidades del cierre de ${anterior.titulo}.`
            : `El cierre de ${anterior.titulo} no está sellado, así que no hay bloqueos automáticos.`;

    return (
        <div>
            <p className="text-sm text-gray-600 mb-4 max-w-3xl">
                Quien aparece aquí no puede comprar en esta venta; en la siguiente ya no aplica. {origen}
            </p>

            {!soloLectura && (
                <form onSubmit={bloquear} className="mb-4 flex flex-wrap items-start gap-2">
                    <div className="relative flex-1 min-w-[240px]">
                        {elegido ? (
                            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm">
                                <span className="truncate text-gray-900">{elegido.nombre} <span className="text-gray-500">{elegido.ficha}</span></span>
                                <button type="button" onClick={() => setElegido(null)} className="p-0.5 text-gray-400 hover:text-gray-700" aria-label="Quitar empleado"><X size={14} /></button>
                            </div>
                        ) : (
                            <>
                                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={busqueda}
                                    onChange={(e) => { setBusqueda(e.target.value); setAbierto(true); }}
                                    onFocus={() => setAbierto(true)}
                                    onBlur={() => setAbierto(false)}
                                    placeholder="Empleado a bloquear (nombre o ficha)"
                                    className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                                />
                            </>
                        )}
                        {abierto && resultados && (
                            <ul className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
                                {resultados.length === 0 && <li className="px-3 py-2 text-sm text-gray-400">Sin resultados</li>}
                                {resultados.map(r => (
                                    <li key={r.id}>
                                        <button
                                            type="button"
                                            disabled={r.bloqueado}
                                            onMouseDown={(e) => { e.preventDefault(); if (!r.bloqueado) elegir(r); }}
                                            className="w-full text-left px-3 py-2 hover:bg-gray-50 disabled:cursor-default disabled:hover:bg-white"
                                        >
                                            <p className={`text-sm ${r.bloqueado ? 'text-gray-400' : 'text-gray-900'}`}>{r.nombre}{r.bloqueado ? ' (ya está bloqueado)' : ''}</p>
                                            <p className="text-xs text-gray-500">{[r.ficha, r.departamento].filter(Boolean).join(' · ')}</p>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    <input
                        type="text"
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        placeholder="Motivo"
                        className="flex-1 min-w-[240px] px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none"
                    />
                    <button
                        type="submit"
                        disabled={!elegido || !motivo.trim() || guardando}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {guardando && <Loader2 size={16} className="animate-spin" />} Bloquear
                    </button>
                </form>
            )}

            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}

            <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
                {empleados.length === 0 ? (
                    <div className="py-12 text-center text-sm text-gray-400">Nadie está bloqueado en esta venta.</div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className={TH}>Empleado</th>
                                <th className={TH}>Origen</th>
                                <th className={TH}>Motivo</th>
                                <th className={TH}>Bloqueado por</th>
                                {!soloLectura && <th className={TH}><span className="sr-only">Acciones</span></th>}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {empleados.map(emp => (
                                <tr key={emp.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3">
                                        <p className="text-sm text-gray-900">{emp.nombre}</p>
                                        <p className="text-xs text-gray-500">{[emp.ficha, emp.departamento].filter(Boolean).join(' · ')}</p>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`inline-block px-2 py-0.5 text-xs border rounded-full ${COLOR_ORIGEN[emp.tipo]}`}>
                                            {emp.tipo === 'automatico' ? 'Automático' : 'Manual'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700">{emp.motivo}</td>
                                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                                        {emp.por ? <>{emp.por}<br />{formatFechaHora(emp.fecha)}</> : '—'}
                                    </td>
                                    {!soloLectura && (
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => desbloquear(emp)} className="text-xs font-medium text-slate-600 hover:text-slate-900 hover:underline">
                                                Desbloquear
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
};

export default BloqueadosTab;
